import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useConfirmTotp, useDisableTotp, useRegenerateCodes, useStartTotp } from "../../hooks/useSecurity";
import { errorMessage } from "../../hooks/useServers";

// Los 10 códigos de recuperación: se muestran UNA vez; se pueden copiar o descargar.
export function RecoveryCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`Códigos de recuperación — Panel Orquestador\nCada uno sirve una sola vez.\n\n${text}\n`], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "codigos-de-recuperacion.txt";
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="recovery-codes" className="space-y-3 rounded-lg border border-amber-500/40 p-3">
      <p className="text-xs text-amber-500">Guarda estos 10 códigos en tu gestor de contraseñas. Cada uno sirve una sola vez y NO se vuelven a mostrar.</p>
      <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
        {codes.map((c) => <li key={c} data-testid="recovery-code-item">{c}</li>)}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="recovery-copy" size="sm" variant="outline" onClick={copy}>{copied ? "Copiados ✓" : "Copiar"}</Button>
        <Button data-testid="recovery-download" size="sm" variant="outline" onClick={download}>Descargar</Button>
        <Button data-testid="recovery-done" size="sm" onClick={onDone}>Ya los guardé</Button>
      </div>
    </div>
  );
}

// Verificación en 2 pasos (Google Authenticator): activar con QR, cambiar de teléfono, códigos nuevos y desactivar. Todo pide un código válido.
export function TotpSection({ status }) {
  const start = useStartTotp();
  const confirm = useConfirmTotp();
  const disable = useDisableTotp();
  const regenerate = useRegenerateCodes();
  const [setup, setSetup] = useState(null); // {secret, uri, qr_svg}
  const [codes, setCodes] = useState(null);
  const [ask, setAsk] = useState(null); // "change" | "regen" | "disable"
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const guard = async (action) => {
    setError("");
    try {
      return await action();
    } catch (e) {
      setError(errorMessage(e, "No se pudo completar."));
      return null;
    }
  };
  const begin = async (currentCode) => {
    const data = await guard(() => start.mutateAsync(currentCode ? { current_code: currentCode } : {}));
    if (data) {
      setSetup(data);
      setAsk(null);
      setCode("");
    }
  };
  const finish = async () => {
    const data = await guard(() => confirm.mutateAsync({ code }));
    if (data) {
      setSetup(null);
      setCode("");
      if (data.recovery_codes) setCodes(data.recovery_codes);
    }
  };
  const submitAsk = async () => {
    if (ask === "change") return begin(code);
    const data = await guard(() => (ask === "regen" ? regenerate.mutateAsync({ code }) : disable.mutateAsync({ code })));
    if (data) {
      setAsk(null);
      setCode("");
      if (data.recovery_codes) setCodes(data.recovery_codes);
    }
  };

  return (
    <div data-testid="totp-section" className="space-y-3">
      {status.totp_enabled && !setup && !codes && (
        <>
          <p data-testid="totp-state" className="text-sm">✅ Activada · te quedan {status.recovery_codes_left} códigos de recuperación</p>
          {!ask && (
            <div className="flex flex-wrap gap-2">
              <Button data-testid="totp-change" size="sm" variant="outline" onClick={() => { setError(""); setAsk("change"); }}>Cambiar de teléfono</Button>
              <Button data-testid="totp-regen" size="sm" variant="outline" onClick={() => { setError(""); setAsk("regen"); }}>Códigos de recuperación nuevos</Button>
              <Button data-testid="totp-disable" size="sm" variant="outline" onClick={() => { setError(""); setAsk("disable"); }}>Desactivar</Button>
            </div>
          )}
        </>
      )}
      {!status.totp_enabled && !setup && !codes && (
        <>
          <p className="text-sm text-amber-500">🟡 Sin activar: hoy tu contraseña es lo único que protege el panel.</p>
          <Button data-testid="totp-start" size="sm" disabled={start.isPending} onClick={() => begin()}>Activar verificación en 2 pasos</Button>
        </>
      )}
      {ask && (
        <div data-testid="totp-ask" className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Escribe el código actual de Google Authenticator{ask === "disable" ? " para desactivar los 2 pasos" : ""}.</p>
          <div className="flex gap-2">
            <Input data-testid="totp-ask-code" inputMode="numeric" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} className="w-32 text-center tracking-widest" />
            <Button data-testid="totp-ask-submit" size="sm" disabled={code.replace(/\D/g, "").length !== 6} onClick={submitAsk}>Continuar</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAsk(null); setCode(""); }}>Cancelar</Button>
          </div>
        </div>
      )}
      {setup && (
        <div data-testid="totp-setup" className="space-y-3 rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">1) Abre Google Authenticator → «+» → «Escanear código QR». 2) Escribe el código de 6 dígitos que muestre «Panel Orquestador».</p>
          {setup.qr_svg && <img data-testid="totp-qr" alt="Código QR para Google Authenticator" className="h-44 w-44 rounded bg-white p-1" src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`} />}
          <p className="text-xs text-muted-foreground">¿No puedes escanear? Escribe esta clave a mano: <span data-testid="totp-secret" className="font-mono text-foreground break-all">{setup.secret}</span></p>
          <div className="flex gap-2">
            <Input data-testid="totp-confirm-code" inputMode="numeric" maxLength={7} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} className="w-32 text-center tracking-widest" />
            <Button data-testid="totp-confirm" size="sm" disabled={code.replace(/\D/g, "").length !== 6 || confirm.isPending} onClick={finish}>Confirmar y activar</Button>
            <Button size="sm" variant="ghost" onClick={() => { setSetup(null); setCode(""); }}>Cancelar</Button>
          </div>
        </div>
      )}
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}
      {error && <p data-testid="totp-error" className="text-xs text-amber-500">{error}</p>}
    </div>
  );
}
